import React, { useState, useEffect } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Modal
} from "react-bootstrap";
import { FaSave, FaUndo, FaTrash, FaToggleOn, FaToggleOff, FaPlus } from "react-icons/fa";

const FooterAdmin = () => {
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    fetchFooterData();
  }, []);

  const fetchFooterData = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/footer");
      const data = await response.json();
      
      if (data.success) {
        setFooterData(data.data);
      } else {
        setError(data.message || "Failed to fetch footer data");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error fetching footer:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFooterData({
      ...footerData,
      [name]: value
    });
  };

  const handleSocialLinkChange = (index, field, value) => {
    const updatedSocialLinks = [...footerData.socialLinks];
    updatedSocialLinks[index] = {
      ...updatedSocialLinks[index],
      [field]: value
    };
    setFooterData({
      ...footerData,
      socialLinks: updatedSocialLinks
    });
  };

  const handleContactInfoChange = (index, field, value) => {
    const updatedContactInfo = [...footerData.contactInfo];
    updatedContactInfo[index] = {
      ...updatedContactInfo[index],
      [field]: value
    };
    setFooterData({
      ...footerData,
      contactInfo: updatedContactInfo
    });
  };

  const addSocialLink = () => {
    const newSocialLink = {
      id: Date.now(),
      platform: "New Platform",
      icon: "fab fa-new",
      url: "#"
    };
    setFooterData({
      ...footerData,
      socialLinks: [...footerData.socialLinks, newSocialLink]
    });
  };

  const removeSocialLink = (index) => {
    const updatedSocialLinks = [...footerData.socialLinks];
    updatedSocialLinks.splice(index, 1);
    setFooterData({
      ...footerData,
      socialLinks: updatedSocialLinks
    });
  };

  const addContactInfo = () => {
    const newContactInfo = {
      id: Date.now(),
      type: "other",
      icon: "fas fa-info",
      text: "New contact info",
      url: "#"
    };
    setFooterData({
      ...footerData,
      contactInfo: [...footerData.contactInfo, newContactInfo]
    });
  };

  const removeContactInfo = (index) => {
    const updatedContactInfo = [...footerData.contactInfo];
    updatedContactInfo.splice(index, 1);
    setFooterData({
      ...footerData,
      contactInfo: updatedContactInfo
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/footer", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(footerData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("Footer updated successfully!");
        setFooterData(data.data);
      } else {
        setError(data.message || "Failed to update footer");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error updating footer:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/footer/toggle", {
        method: "PATCH"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Footer ${data.data.isActive ? "activated" : "deactivated"} successfully!`);
        setFooterData(data.data);
      } else {
        setError(data.message || "Failed to toggle footer status");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error toggling footer:", err);
    }
  };

  const handleReset = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/footer/reset", {
        method: "POST"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("Footer reset to default successfully!");
        setFooterData(data.data);
        setShowResetModal(false);
      } else {
        setError(data.message || "Failed to reset footer");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error resetting footer:", err);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden"></span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!footerData) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Failed to load footer data</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="mb-4">Manage Footer</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Footer Status</h5>
          <Button 
            variant={footerData.isActive ? "success" : "secondary"}
            onClick={handleToggleActive}
          >
            {footerData.isActive ? <FaToggleOn /> : <FaToggleOff />} 
            {footerData.isActive ? " Active" : " Inactive"}
          </Button>
        </Card.Header>
        <Card.Body>
          <p>
            Last updated: {new Date(footerData.updatedAt).toLocaleString()}
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Footer Content</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Logo URL</Form.Label>
            <Form.Control
              type="text"
              name="logoUrl"
              value={footerData.logoUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/logo.png"
            />
            {footerData.logoUrl && (
              <div className="mt-2">
                <img 
                  src={footerData.logoUrl} 
                  alt="Logo Preview" 
                  style={{ maxWidth: "200px", maxHeight: "100px" }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://picsum.photos/seed/error/200/100.jpg";
                  }}
                />
              </div>
            )}
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="description"
              value={footerData.description}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Copyright Text</Form.Label>
            <Form.Control
              type="text"
              name="copyrightText"
              value={footerData.copyrightText}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Social Links</h5>
          <Button variant="primary" onClick={addSocialLink}>
            <FaPlus /> Add Social Link
          </Button>
        </Card.Header>
        <Card.Body>
          {footerData.socialLinks.map((link, index) => (
            <div key={link.id || index} className="mb-3 border p-3 rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6>Social Link {index + 1}</h6>
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={() => removeSocialLink(index)}
                >
                  <FaTrash />
                </Button>
              </div>
              
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>Platform</Form.Label>
                    <Form.Control
                      type="text"
                      value={link.platform}
                      onChange={(e) => handleSocialLinkChange(index, "platform", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>Icon Class</Form.Label>
                    <Form.Control
                      type="text"
                      value={link.icon}
                      onChange={(e) => handleSocialLinkChange(index, "icon", e.target.value)}
                      placeholder="fab fa-platform"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label>URL</Form.Label>
                    <Form.Control
                      type="text"
                      value={link.url}
                      onChange={(e) => handleSocialLinkChange(index, "url", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          ))}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Contact Information</h5>
          <Button variant="primary" onClick={addContactInfo}>
            <FaPlus /> Add Contact Info
          </Button>
        </Card.Header>
        <Card.Body>
          {footerData.contactInfo.map((info, index) => (
            <div key={info.id || index} className="mb-3 border p-3 rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6>Contact Info {index + 1}</h6>
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={() => removeContactInfo(index)}
                >
                  <FaTrash />
                </Button>
              </div>
              
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={info.type}
                      onChange={(e) => handleContactInfoChange(index, "type", e.target.value)}
                    >
                      <option value="address">Address</option>
                      <option value="phone">Phone</option>
                      <option value="email">Email</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label>Icon Class</Form.Label>
                    <Form.Control
                      type="text"
                      value={info.icon}
                      onChange={(e) => handleContactInfoChange(index, "icon", e.target.value)}
                      placeholder="fas fa-icon"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label>Text</Form.Label>
                    <Form.Control
                      type="text"
                      value={info.text}
                      onChange={(e) => handleContactInfoChange(index, "text", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label>URL</Form.Label>
                    <Form.Control
                      type="text"
                      value={info.url}
                      onChange={(e) => handleContactInfoChange(index, "url", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          ))}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between">
        <Button variant="secondary" onClick={() => setShowResetModal(true)}>
          <FaUndo /> Reset to Default
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="visually-hidden"></span>
            </>
          ) : (
            <><FaSave /> Save Changes</>
          )}
        </Button>
      </div>

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset to Default</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset the footer to default values? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReset}>
            Reset
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FooterAdmin;